import { useEffect, useRef, useState, useCallback } from 'react';
import { Loader2 } from 'lucide-react';
import Dialog from '../Dialog/Dialog';

const FlywirePayment = ({
  frontendKey,
  sessionId,
  customerData = {},
  onSuccess,
  onError,
  appearance = {}
}) => {
  const containerRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [isMounted, setIsMounted] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [invoiceId, setInvoiceId] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  const initializeFlywire = useCallback(async () => {
    try {
      if (!window.FlywireSDK) {
        throw new Error('Flywire SDK not loaded');
      }

      if (!frontendKey || !sessionId) {
        throw new Error('Frontend key and session ID are required');
      }

      if (!containerRef.current) {
        console.log('⏳ Waiting for container to mount...');
        return;
      }

      console.log('🚀 Initializing Flywire SDK with:', { frontendKey, sessionId });

      const sdk = await window.FlywireSDK(frontendKey);

      const defaultAppearance = {
        fonts: [
          {
            url: "https://fonts.googleapis.com/css2?family=Roboto:ital,wght@0,100;0,300;0,400;0,500;0,700&display=swap",
            fontFamily: "Roboto",
          },
        ],
        variables: { primaryColor: "#5a81f0" },
        ...appearance
      };

      const elements = await sdk.elements({
        appearance: defaultAppearance,
        locale: "en",
      });

      const element = await elements.create("payment", {
        sessionId: sessionId,
        displayMode: "container",
        fields: {
          first_name: { hidden: false },
          last_name: { hidden: false },
          country: { hidden: true },
          address: { hidden: true },
          city: { hidden: true },
          phone: { hidden: false },
          email: { hidden: false },
          state: { hidden: false },
          zip: { hidden: false },
        },
      });

      element.onEvent("success", async (sessionResult) => {
        console.log('Flywire payment success - sessionResult:', sessionResult);
        setLoading(false);

        try {
          console.log('🚀 Sending sessionResult to backend:', sessionResult);

          const response = await fetch('https://api.ctsdmcwpc.com/api/flywire-success', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(sessionResult),
          });

          console.log('📡 Backend response status:', response.status, response.statusText);

          if (response.ok) {
            const backendResponse = await response.json();
            console.log('✅ Backend confirmation successful:', backendResponse);
            console.log('📋 Full backend response:', JSON.stringify(backendResponse, null, 2));

            // Extract invoice ID from Zoho order response
            const zohoInvoiceId = backendResponse.zohoOrder?.invoice_id ||
                                  backendResponse.invoice_id ||
                                  backendResponse.invoiceId ||
                                  'N/A';

            // Set invoice ID and show success dialog
            setInvoiceId(zohoInvoiceId);
            setShowSuccessDialog(true);

            if (onSuccess) {
              onSuccess(sessionResult);
            }
          } else {
            const errorResponse = await response.text();
            console.error('❌ Backend confirmation failed:', response.status, errorResponse);
            throw new Error(`Backend confirmation failed: ${response.status} - ${errorResponse}`);
          }
        } catch (error) {
          console.error('🔥 Error sending sessionResult to backend:', error);
          console.error('🔥 Error details:', error.message);

          // Show error dialog instead of success
          setErrorMessage('Failed to process your order. Please contact support.');
          setShowErrorDialog(true);

          // Don't call onError - we're handling errors with dialog now
          // if (onError) {
          //   onError(error);
          // }
        }
      });

      element.onEvent("error", async (error) => {
        console.error('Flywire payment error:', error);
        setLoading(false);

        // Extract error message from various possible formats
        let errorMsg = 'Payment failed. Your order was not placed.';
        if (error) {
          if (typeof error === 'string') {
            errorMsg = error;
          } else if (error.message) {
            errorMsg = error.message;
          } else if (error.error) {
            errorMsg = error.error;
          } else if (error.description) {
            errorMsg = error.description;
          }
        }

        // Show error dialog FIRST before async operations
        setErrorMessage(errorMsg);
        setShowErrorDialog(true);

        try {
          // MANDATORY: Send error details to backend for troubleshooting
          console.log('🚨 Sending error to backend:', error);

          const response = await fetch('https://api.ctsdmcwpc.com/api/flywire-error', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(error),
          });

          console.log('📡 Error endpoint response status:', response.status, response.statusText);

          if (response.ok) {
            const backendResponse = await response.json();
            console.log('✅ Error logged to backend successfully:', backendResponse);
          } else {
            const errorResponse = await response.text();
            console.error('❌ Failed to log error to backend:', response.status, errorResponse);
          }
        } catch (backendError) {
          console.error('🔥 Error sending error to backend:', backendError);
          console.error('🔥 Backend error details:', backendError.message);
        }

        // Don't call onError - we're handling errors with dialog now
        // if (onError) {
        //   onError(error);
        // }
      });

      element.onEvent("ready", () => {
        console.log('✅ Flywire payment form is ready');
        setLoading(false);
      });

      // Mount the element to the container (using container ID as string, not ref)
      console.log('📦 Mounting Flywire element to container');
      await element.mount("flywire-container");
      console.log('✅ Flywire element mounted successfully');

      setLoading(false);
    } catch (err) {
      console.error('Failed to initialize Flywire:', err);
      setErrorMessage(err.message || 'Failed to initialize payment. Please refresh and try again.');
      setShowErrorDialog(true);
      setLoading(false);
    }
  }, [frontendKey, sessionId, appearance, onSuccess, onError]);

  // Set mounted flag when component mounts
  useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);

  // Load SDK and initialize when container is ready
  useEffect(() => {
    if (!isMounted || !containerRef.current) {
      return;
    }

    const loadFlywireSDK = () => {
      return new Promise((resolve, reject) => {
        if (window.FlywireSDK) {
          console.log('✅ Flywire SDK already loaded');
          resolve();
          return;
        }

        console.log('📥 Loading Flywire SDK...');
        const script = document.createElement('script');
        // Use sandbox for testing, production for live
        script.src = 'https://artifacts.flywire.com/sdk/js/v0/main.js';
        script.async = true;
        script.crossOrigin = 'anonymous';
        script.type = 'text/javascript';

        script.onload = () => {
          console.log('✅ Flywire SDK loaded successfully');
          resolve();
        };
        script.onerror = () => {
          console.error('❌ Failed to load Flywire SDK');
          reject(new Error('Failed to load Flywire SDK'));
        };
        document.head.appendChild(script);
      });
    };

    loadFlywireSDK()
      .then(() => {
        initializeFlywire();
      })
      .catch((err) => {
        console.error('❌ SDK loading error:', err);
        setErrorMessage(err.message || 'Failed to load payment system. Please refresh and try again.');
        setShowErrorDialog(true);
        setLoading(false);
      });
  }, [isMounted, initializeFlywire]);

  const handleRetry = () => {
    setShowErrorDialog(false);
    setErrorMessage('');
    setLoading(true);
    initializeFlywire();
  };

  const handleSuccessDialogClose = () => {
    setShowSuccessDialog(false);
    // Optionally redirect or perform other actions after closing
    // window.location.href = '/booking-confirmation';
  };

  const handleErrorDialogClose = () => {
    setShowErrorDialog(false);
    setErrorMessage('');
  };

  return (
    <div className="flywire-payment-container">
      {loading && (
        <div className="flywire-loading">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
          <p className="mt-2 text-gray-600">Loading payment form...</p>
        </div>
      )}

      <div
        ref={containerRef}
        id="flywire-container"
        className="flywire-payment-element"
        style={{ display: loading ? 'none' : 'block' }}
      />

      {/* Success Dialog */}
      <Dialog
        isOpen={showSuccessDialog}
        onClose={handleSuccessDialogClose}
        type="success"
        title="Order Placed Successfully!"
        message="Your payment has been processed and your order has been placed."
        invoiceId={invoiceId}
      />

      {/* Error Dialog */}
      <Dialog
        isOpen={showErrorDialog}
        onClose={handleErrorDialogClose}
        type="error"
        title="Order Not Placed"
        message={errorMessage}
        showRetryButton={true}
        onRetry={handleRetry}
      />
    </div>
  );
};

export default FlywirePayment;